import { ReactNode, useState } from 'react'

import * as HoverCard from '@radix-ui/react-hover-card'
import { AnimatePresence } from 'framer-motion'
import {
  EmployeesEmployeeNameQuickViewPopoverFragment,
  EmployeesEmployeeQuickViewDocument,
  EmployeesEmployeeQuickViewPopoverFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyQuery } from 'modules/core/hooks/useQuery'

import { Card } from '../Card'

interface Props {
  employee:
    | EmployeesEmployeeQuickViewPopoverFragment
    | EmployeesEmployeeNameQuickViewPopoverFragment
  children: ReactNode
  onViewProfile?: () => void
  usePreferredName?: boolean
}

export const Popover: React.FC<Props> = ({
  employee: { id },
  children,
  onViewProfile,
  usePreferredName,
}) => {
  const [loadEmployee, { data }] = useLazyQuery(EmployeesEmployeeQuickViewDocument, {
    variables: { id },
    fetchPolicy: 'cache-first',
  })

  const { canSeeEmployeeProfile } = useCurrentSession()
  const employee = data?.employees.employee

  const [open, setOpen] = useState(false)

  const handleSeeMore = () => {
    setOpen(false)
    onViewProfile?.()
  }

  if (!canSeeEmployeeProfile) return <>{children}</>

  return (
    <HoverCard.Root open={open} onOpenChange={setOpen}>
      <HoverCard.Trigger
        asChild
        onMouseEnter={() => {
          loadEmployee()
        }}
      >
        {children}
      </HoverCard.Trigger>
      <HoverCard.Portal forceMount>
        <AnimatePresence>
          {open && employee && (
            <HoverCard.Content
              forceMount
              side="top"
              sideOffset={10}
              collisionPadding={10}
              style={{ zIndex: 1000 }}
            >
              <Card
                employee={employee}
                onSeeMoreClick={handleSeeMore}
                usePreferredName={usePreferredName}
              />
            </HoverCard.Content>
          )}
        </AnimatePresence>
      </HoverCard.Portal>
    </HoverCard.Root>
  )
}
